import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { useGetBuildingsByIdQuery, useGetInvestmentsQuery, useGetSpacesByIdQuery } from '@client/api';
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CardTitle,
    EmptyPlaceholder,
    Pagination,
    Table,
    TableBody,
    TableHead,
    TableHeadCell,
    TableHeadRow
} from '@client/toolkit';

import { InvestmentItem } from './investment-item.component';

import { InvestmentsPaths } from '../../../investments/investments.router';

export interface BuildingInvestmentsCardProps {
  className?: string;
  buildingId?: string;
  spaceId?: never;
}

export interface SpaceInvestmentsCardProps {
  className?: string;
  spaceId?: string;
  buildingId?: never;
}

export type InvestmentsCardProps = BuildingInvestmentsCardProps | SpaceInvestmentsCardProps;

export function InvestmentsCard({ className, spaceId: parentSpaceId, buildingId: parentBuildingId }: InvestmentsCardProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const navigate = useNavigate();

  const pageSize = 25;
  const [page, setPage] = useState(0);

  const { data: space, isFetching: isFetchingSpace, isLoading: isLoadingSpace } = useGetSpacesByIdQuery({
    id: parentSpaceId ?? '',
  }, { skip: !parentSpaceId });

  const { data: building, isFetching: isFetchingBuilding, isLoading: isLoadingBuilding } = useGetBuildingsByIdQuery({
    id: parentBuildingId ?? '',
  }, { skip: !parentBuildingId });

  const ancestorPath = useMemo(() => space?.path || building?.path, [building?.path, space?.path]);

  const { data: investmentsData, isFetching: isFetchingInvestments, isLoading: isLoadingInvestments,
  } = useGetInvestmentsQuery({
    'spaceAncestorPath.Equal': ancestorPath,
    'pagination.Limit': pageSize,
    'pagination.Offset': page * pageSize,
  }, { skip: !ancestorPath });

  const investments = useMemo(() => investmentsData?.items ?? [], [investmentsData]);

  const isListEmpty = useMemo(() => investments != null && investments.length <= 0, [investments]);
  const isLoading = isLoadingInvestments || isLoadingBuilding || isLoadingSpace;
  const isFetching = isFetchingInvestments || isFetchingBuilding || isFetchingSpace;

  const totalCount = investmentsData?.totalCount ?? 0;

  const handleClickCreateInvestment = () => {
    let search: string | undefined;
    if (parentSpaceId) { search = `spaceId=${parentSpaceId}`; }
    else if (parentBuildingId) { search = `buildingId=${parentBuildingId}`; }

    navigate({
      pathname: generatePath(InvestmentsPaths.InvestmentCreate),
      search
    });
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex-1" variant="dense">
          {tGlobal('entity.investment', { context: 'plural' })}
        </CardTitle>
        <CardActions>
          <Button color="primary-dark" disabledVariant="dark" size="sm"
            disabled={isFetching || ancestorPath == null}
            onClick={handleClickCreateInvestment}
          >
            {tCommon('create')}
          </Button>
        </CardActions>
      </CardHeader>
      <CardContent variant="fill">
        <Table className="overflow-hidden rounded-md w-full" layout="fixed">
          {!isLoading && !isFetching && isListEmpty && (
            <caption className="px-4 py-4 [caption-side:bottom]">
              <EmptyPlaceholder>{tCommon('noEntitySpecified', { entity: tGlobal('entity.investment', { context: 'plural' }).toLowerCase() })}</EmptyPlaceholder>
            </caption>
          )}

          <TableHead>
            <TableHeadRow>
              <TableHeadCell className="w-36">{tGlobal('investment.durationStart')}</TableHeadCell>
              <TableHeadCell>{tGlobal('investment.name')}</TableHeadCell>
              <TableHeadCell className="w-[30%]">{tGlobal('investment.spaces')}</TableHeadCell>
              <TableHeadCell className="w-32">{tGlobal('investment.totalArea')}</TableHeadCell>
              <TableHeadCell className="w-36">{tGlobal('investment.returnDurationEnd')}</TableHeadCell>
            </TableHeadRow>
          </TableHead>

          <TableBody>
            {(isLoading || (isFetching && isListEmpty)) &&
              Array.from(Array(3).keys()).map((x) => (
                <InvestmentItem key={x} isFetching />
              ))}

            {!isLoading &&
              investments?.map((investment, index) => (
                <InvestmentItem
                  key={investment.id}
                  investment={investment}
                  isFetching={isFetching}
                />
              ))}
          </TableBody>
        </Table>

        <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
      </CardContent>
    </Card>
  );
}
